import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Key, Globe, Database, Menu, X, Eye, EyeOff, Info, ChevronDown, CheckCircle2, XCircle, Loader2, AlertTriangle, Copy, Check, RefreshCw, Trash2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const sidebarItems = [
  { id: "ai-providers", label: "AI Providers", icon: Key },
  { id: "custom-domain", label: "Custom Domain", icon: Globe },
  { id: "datawarehouse", label: "Self Hosting", icon: Database },
];

const CNAME_TARGET_SUFFIX = ".databull.ai";

interface DomainConfig {
  custom_domain: string | null;
  custom_domain_status: string;
  custom_domain_cname_target: string | null;
  urlslug: string | null;
}

interface SavedKey {
  key_name: string;
  masked_value: string;
  updated_at: string;
}

const Deployment = () => {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return sidebarItems.find((i) => i.id === fromUrl) ? fromUrl! : sidebarItems[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const activeItem = sidebarItems.find((i) => i.id === active);

  const selectTab = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => { prev.set("tab", id); return prev; });
  };

  // Key store state
  const [savedKeys, setSavedKeys] = useState<Record<string, SavedKey>>({});
  const [keysLoading, setKeysLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  // AI Providers state
  const [claudeKey, setClaudeKey] = useState("");
  const [openaiKey, setOpenaiKey] = useState("");
  const [showClaude, setShowClaude] = useState(false);
  const [showOpenai, setShowOpenai] = useState(false);

  // Langfuse state
  const [langfusePublicKey, setLangfusePublicKey] = useState("");
  const [langfuseSecretKey, setLangfuseSecretKey] = useState("");
  const [langfuseHost, setLangfuseHost] = useState("");
  const [showLangfuseSecret, setShowLangfuseSecret] = useState(false);

  // Custom domain state
  const [customDomain, setCustomDomain] = useState("");
  const [domainConfig, setDomainConfig] = useState<DomainConfig | null>(null);
  const [domainLoading, setDomainLoading] = useState(true);
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainRemoving, setDomainRemoving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // URL slug state
  const [urlslugInput, setUrlslugInput] = useState("");
  const [urlslugSaving, setUrlslugSaving] = useState(false);

  // Resend state
  const [resendKey, setResendKey] = useState("");
  const [showResendKey, setShowResendKey] = useState(false);

  // Starrocks state
  const [srHost, setSrHost] = useState("");
  const [srPort, setSrPort] = useState("");
  const [srUser, setSrUser] = useState("");
  const [srPassword, setSrPassword] = useState("");
  const [showSrPassword, setShowSrPassword] = useState(false);

  // --- Key Store operations ---

  const fetchKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-keys", {
        body: { action: "list" },
      });
      if (error) throw error;
      const map: Record<string, SavedKey> = {};
      (data?.keys || []).forEach((k: SavedKey) => {
        map[k.key_name] = k;
      });
      setSavedKeys(map);
    } catch {
      // Silent fail on load
    }
    setKeysLoading(false);
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const saveKey = async (keyName: string, keyValue: string) => {
    if (!keyValue.trim()) {
      toast.error("Please enter a value");
      return;
    }
    setSavingKey(keyName);
    try {
      const { data, error } = await supabase.functions.invoke("manage-keys", {
        body: { action: "save", key_name: keyName, key_value: keyValue.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSavedKeys((prev) => ({
        ...prev,
        [keyName]: { key_name: keyName, masked_value: data.masked_value, updated_at: new Date().toISOString() },
      }));
      toast.success("Key saved securely");
      // Clear the input after saving
      clearKeyInput(keyName);
    } catch (e: any) {
      toast.error(e.message || "Failed to save key");
    }
    setSavingKey(null);
  };

  const deleteKey = async (keyName: string) => {
    setDeletingKey(keyName);
    try {
      const { data, error } = await supabase.functions.invoke("manage-keys", {
        body: { action: "delete", key_name: keyName },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSavedKeys((prev) => {
        const next = { ...prev };
        delete next[keyName];
        return next;
      });
      toast.success("Key removed");
    } catch (e: any) {
      toast.error(e.message || "Failed to delete key");
    }
    setDeletingKey(null);
  };

  const saveMultipleKeys = async (keys: { name: string; value: string }[]) => {
    const toSave = keys.filter((k) => k.value.trim());
    if (toSave.length === 0) {
      toast.error("Please enter at least one value");
      return;
    }
    setSavingKey(toSave[0].name);
    try {
      for (const k of toSave) {
        const { data, error } = await supabase.functions.invoke("manage-keys", {
          body: { action: "save", key_name: k.name, key_value: k.value.trim() },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        setSavedKeys((prev) => ({
          ...prev,
          [k.name]: { key_name: k.name, masked_value: data.masked_value, updated_at: new Date().toISOString() },
        }));
      }
      toast.success(`${toSave.length} key${toSave.length > 1 ? "s" : ""} saved securely`);
      toSave.forEach((k) => clearKeyInput(k.name));
    } catch (e: any) {
      toast.error(e.message || "Failed to save keys");
    }
    setSavingKey(null);
  };

  const clearKeyInput = (keyName: string) => {
    switch (keyName) {
      case "claude_api_key": setClaudeKey(""); break;
      case "openai_api_key": setOpenaiKey(""); break;
      case "langfuse_public_key": setLangfusePublicKey(""); break;
      case "langfuse_secret_key": setLangfuseSecretKey(""); break;
      case "langfuse_host": setLangfuseHost(""); break;
      case "resend_api_key": setResendKey(""); break;
      case "starrocks_host": setSrHost(""); break;
      case "starrocks_port": setSrPort(""); break;
      case "starrocks_user": setSrUser(""); break;
      case "starrocks_password": setSrPassword(""); break;
    }
  };

  // --- Custom domain operations ---

  const fetchDomainConfig = useCallback(async () => {
    if (!profile?.client_id) return;
    setDomainLoading(true);
    const { data } = await supabase
      .from("client")
      .select("custom_domain, custom_domain_status, custom_domain_cname_target, urlslug")
      .eq("id", profile.client_id)
      .single();
    if (data) {
      setDomainConfig(data as unknown as DomainConfig);
      setUrlslugInput(data.urlslug ?? "");
    }
    setDomainLoading(false);
  }, [profile?.client_id]);

  const handleSaveUrlslug = async () => {
    if (!profile?.client_id) return;
    const slug = urlslugInput.trim().toLowerCase();
    if (!slug) {
      toast.error("Please enter a URL slug");
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(slug)) {
      toast.error("Slug must be 3–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphens)");
      return;
    }
    setUrlslugSaving(true);
    // Check uniqueness — another client must not already own this slug
    const { data: existing } = await supabase
      .from("client")
      .select("id")
      .eq("urlslug", slug)
      .neq("id", profile.client_id)
      .maybeSingle();
    if (existing) {
      toast.error("This URL slug is already taken. Please choose a different one.");
      setUrlslugSaving(false);
      return;
    }
    const { error } = await supabase
      .from("client")
      .update({ urlslug: slug } as any)
      .eq("id", profile.client_id);
    if (error) {
      toast.error(error.message);
      setUrlslugSaving(false);
      return;
    }
    toast.success(`URL slug updated to "${slug}". Redirecting to new workspace URL…`);
    // Update the shared cookie and navigate to the new subdomain
    document.cookie = `dfg_urlslug=${slug}; domain=.databull.ai; path=/; SameSite=Lax; Secure; max-age=31536000`;
    await fetchDomainConfig();
    setUrlslugSaving(false);
    // Give the toast a moment to show then redirect
    setTimeout(() => {
      window.location.href = `https://${slug}.databull.ai/deployment`;
    }, 1500);
  };

  useEffect(() => {
    if (active === "custom-domain") fetchDomainConfig();
  }, [active, fetchDomainConfig]);

  const handleSaveDomain = async () => {
    if (!profile?.client_id) return;
    const domain = customDomain.trim().toLowerCase();
    if (!domain) {
      toast.error("Please enter a domain name");
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(domain)) {
      toast.error("Please enter a valid domain (e.g. analytics.yourcompany.com)");
      return;
    }
    setDomainSaving(true);
    const cnameTarget = `${profile.client_id}${CNAME_TARGET_SUFFIX}`;
    const { error } = await supabase
      .from("client")
      .update({
        custom_domain: domain,
        custom_domain_status: "pending",
        custom_domain_cname_target: cnameTarget,
      } as any)
      .eq("id", profile.client_id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Domain saved. Add the CNAME record below then verify.");
      setCustomDomain("");
      await fetchDomainConfig();
    }
    setDomainSaving(false);
  };

  const handleVerifyDomain = async () => {
    setDomainVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-domain", {
        body: {},
      });
      if (error) throw error;
      if (data?.verified) {
        toast.success(data.message);
      } else {
        toast.error(data?.message || "Verification failed");
      }
      await fetchDomainConfig();
    } catch (e: any) {
      toast.error(e.message || "Verification failed");
    }
    setDomainVerifying(false);
  };

  const handleRemoveDomain = async () => {
    if (!profile?.client_id) return;
    setDomainRemoving(true);
    const { error } = await supabase
      .from("client")
      .update({
        custom_domain: null,
        custom_domain_status: "none",
        custom_domain_cname_target: null,
      } as any)
      .eq("id", profile.client_id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Custom domain removed");
      await fetchDomainConfig();
    }
    setDomainRemoving(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  // --- Saved key badge ---
  const SavedBadge = ({ keyName }: { keyName: string }) => {
    const saved = savedKeys[keyName];
    if (!saved) return null;
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20 font-mono">
          <CheckCircle2 className="h-2.5 w-2.5" />
          {saved.masked_value}
        </Badge>
        <button
          onClick={() => deleteKey(keyName)}
          disabled={deletingKey === keyName}
          className="text-muted-foreground hover:text-destructive transition-colors"
          title="Remove key"
        >
          {deletingKey === keyName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deployment</h2>
            <Button variant="ghost" size="icon" className="h-6 w-6 md:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2 pb-4 overflow-y-auto">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => selectTab(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                  active === item.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 md:hidden">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium">{activeItem?.label}</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mx-auto max-w-2xl space-y-6">
            {/* AI Providers */}
            {active === "ai-providers" && (
              <>
                <div>
                  <h1 className="text-lg font-semibold">AI Providers</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure API keys for the AI models used by your workspace.
                  </p>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3">
                  <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Use your own API keys or leave blank to use DataBull's keys. If your keys fail, DataBull keys will automatically be used based on your wallet balance. Keys are encrypted at rest and bound to your workspace.
                  </p>
                </div>

                {keysLoading ? (
                  <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading keys...
                  </div>
                ) : (
                  <>
                    <Collapsible className="rounded-lg border border-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold">Claude API Key</p>
                            <SavedBadge keyName="claude_api_key" />
                          </div>
                          <p className="text-sm text-muted-foreground">Used for reasoning and chat responses.</p>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="claude-key">API Key</Label>
                            <div className="relative">
                              <Input
                                id="claude-key"
                                type={showClaude ? "text" : "password"}
                                placeholder={savedKeys.claude_api_key ? "Enter new key to replace..." : "sk-ant-..."}
                                value={claudeKey}
                                onChange={(e) => setClaudeKey(e.target.value)}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowClaude(!showClaude)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showClaude ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => saveKey("claude_api_key", claudeKey)}
                              disabled={savingKey === "claude_api_key" || !claudeKey.trim()}
                            >
                              {savingKey === "claude_api_key" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                              {savingKey === "claude_api_key" ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Collapsible className="rounded-lg border border-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold">OpenAI API Key</p>
                            <SavedBadge keyName="openai_api_key" />
                          </div>
                          <p className="text-sm text-muted-foreground">Used for text embeddings.</p>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="openai-key">API Key</Label>
                            <div className="relative">
                              <Input
                                id="openai-key"
                                type={showOpenai ? "text" : "password"}
                                placeholder={savedKeys.openai_api_key ? "Enter new key to replace..." : "sk-..."}
                                value={openaiKey}
                                onChange={(e) => setOpenaiKey(e.target.value)}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowOpenai(!showOpenai)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showOpenai ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => saveKey("openai_api_key", openaiKey)}
                              disabled={savingKey === "openai_api_key" || !openaiKey.trim()}
                            >
                              {savingKey === "openai_api_key" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                              {savingKey === "openai_api_key" ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Collapsible className="rounded-lg border border-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold">Langfuse</p>
                            {(savedKeys.langfuse_public_key || savedKeys.langfuse_secret_key) && (
                              <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                Configured
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">Connect Langfuse for AI tracing and observability.</p>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="langfuse-public-key">Public Key</Label>
                              <SavedBadge keyName="langfuse_public_key" />
                            </div>
                            <Input
                              id="langfuse-public-key"
                              placeholder={savedKeys.langfuse_public_key ? "Enter new key to replace..." : "pk-lf-..."}
                              value={langfusePublicKey}
                              onChange={(e) => setLangfusePublicKey(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="langfuse-secret-key">Secret Key</Label>
                              <SavedBadge keyName="langfuse_secret_key" />
                            </div>
                            <div className="relative">
                              <Input
                                id="langfuse-secret-key"
                                type={showLangfuseSecret ? "text" : "password"}
                                placeholder={savedKeys.langfuse_secret_key ? "Enter new key to replace..." : "sk-lf-..."}
                                value={langfuseSecretKey}
                                onChange={(e) => setLangfuseSecretKey(e.target.value)}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowLangfuseSecret(!showLangfuseSecret)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showLangfuseSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="langfuse-host">Host</Label>
                              <SavedBadge keyName="langfuse_host" />
                            </div>
                            <Input
                              id="langfuse-host"
                              placeholder={savedKeys.langfuse_host ? "Enter new value to replace..." : "https://cloud.langfuse.com"}
                              value={langfuseHost}
                              onChange={(e) => setLangfuseHost(e.target.value)}
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => saveMultipleKeys([
                                { name: "langfuse_public_key", value: langfusePublicKey },
                                { name: "langfuse_secret_key", value: langfuseSecretKey },
                                { name: "langfuse_host", value: langfuseHost },
                              ])}
                              disabled={savingKey !== null || (!langfusePublicKey.trim() && !langfuseSecretKey.trim() && !langfuseHost.trim())}
                            >
                              {savingKey ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                              {savingKey ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}
              </>
            )}

            {/* Custom Domain */}
            {active === "custom-domain" && (
              <>
                <div>
                  <h1 className="text-lg font-semibold">Custom Domain</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Map your own domain to this workspace so your team can access it via a branded URL.
                  </p>
                </div>

                {/* URL Slug */}
                <div className="rounded-lg border border-border">
                  <div className="p-4 border-b border-border">
                    <p className="text-base font-semibold">Workspace URL</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Your workspace is accessible at <span className="font-mono text-foreground">https://<span className="text-primary">{domainConfig?.urlslug ?? "…"}</span>.databull.ai</span>. Change the slug below.
                    </p>
                  </div>
                  <div className="px-4 pb-4 pt-3 space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="urlslug">URL slug</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center flex-1 rounded-md border border-input overflow-hidden focus-within:ring-1 focus-within:ring-ring">
                          <span className="px-3 py-2 text-sm text-muted-foreground bg-muted border-r border-input shrink-0">
                            https://
                          </span>
                          <input
                            id="urlslug"
                            type="text"
                            className="flex-1 px-3 py-2 text-sm bg-background outline-none"
                            placeholder="your-workspace"
                            value={urlslugInput}
                            onChange={(e) => setUrlslugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                            onKeyDown={(e) => e.key === "Enter" && handleSaveUrlslug()}
                          />
                          <span className="px-3 py-2 text-sm text-muted-foreground bg-muted border-l border-input shrink-0">
                            .databull.ai
                          </span>
                        </div>
                        <Button
                          onClick={handleSaveUrlslug}
                          disabled={urlslugSaving || !urlslugInput.trim() || urlslugInput.trim() === (domainConfig?.urlslug ?? "")}
                        >
                          {urlslugSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Saving…</> : "Save"}
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Lowercase letters, numbers, and hyphens only (3–50 characters). Changing the slug will redirect you to the new URL.
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                <div>
                  <p className="text-base font-semibold">Custom Domain</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Optionally map your own domain to this workspace.
                  </p>
                </div>

                {domainLoading ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading domain configuration...
                  </div>
                ) : domainConfig?.custom_domain && domainConfig.custom_domain_status !== "none" ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-border">
                      <div className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <Globe className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-semibold font-mono">{domainConfig.custom_domain}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {domainConfig.custom_domain_status === "verified" && (
                                <Badge className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/10">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Verified
                                </Badge>
                              )}
                              {domainConfig.custom_domain_status === "pending" && (
                                <Badge className="gap-1 text-[10px] bg-yellow-500/10 text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/10">
                                  <AlertTriangle className="h-3 w-3" />
                                  Pending Verification
                                </Badge>
                              )}
                              {domainConfig.custom_domain_status === "failed" && (
                                <Badge className="gap-1 text-[10px] bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/10">
                                  <XCircle className="h-3 w-3" />
                                  Verification Failed
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={handleVerifyDomain} disabled={domainVerifying} className="gap-1.5 text-xs">
                            {domainVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            {domainVerifying ? "Verifying..." : "Verify DNS"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleRemoveDomain} disabled={domainRemoving} className="gap-1.5 text-xs text-destructive hover:text-destructive">
                            {domainRemoving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>

                    {domainConfig.custom_domain_status !== "verified" && (
                      <div className="rounded-lg border border-border">
                        <div className="p-4 border-b border-border">
                          <p className="text-sm font-semibold">DNS Configuration Required</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Add the following CNAME record to your domain's DNS settings. DNS changes can take up to 48 hours to propagate.
                          </p>
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="rounded-md border border-border overflow-hidden">
                            <div className="grid grid-cols-3 bg-muted/50 border-b border-border">
                              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Type</div>
                              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Name / Host</div>
                              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Value / Target</div>
                            </div>
                            <div className="grid grid-cols-3">
                              <div className="px-3 py-2.5 text-sm font-mono">CNAME</div>
                              <div className="px-3 py-2.5 flex items-center gap-1.5">
                                <code className="text-sm font-mono truncate">{domainConfig.custom_domain?.split(".")[0] || "—"}</code>
                                <button onClick={() => copyToClipboard(domainConfig.custom_domain?.split(".")[0] || "", "name")} className="shrink-0 text-muted-foreground hover:text-foreground transition-colors" title="Copy">
                                  {copied === "name" ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                                </button>
                              </div>
                              <div className="px-3 py-2.5 flex items-center gap-1.5">
                                <code className="text-sm font-mono truncate">{domainConfig.custom_domain_cname_target || "—"}</code>
                                <button onClick={() => copyToClipboard(domainConfig.custom_domain_cname_target || "", "target")} className="shrink-0 text-muted-foreground hover:text-foreground transition-colors" title="Copy">
                                  {copied === "target" ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-start gap-2 rounded-md bg-muted/50 border border-border p-3">
                            <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                            <div className="text-xs text-muted-foreground space-y-1">
                              <p><strong>Step 1:</strong> Log in to your DNS provider (e.g. Cloudflare, GoDaddy, Namecheap, Route 53).</p>
                              <p><strong>Step 2:</strong> Add a CNAME record with the name and value shown above.</p>
                              <p><strong>Step 3:</strong> Wait for DNS propagation (usually 5-30 minutes, can take up to 48 hours).</p>
                              <p><strong>Step 4:</strong> Click "Verify DNS" to check your configuration.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {domainConfig.custom_domain_status === "verified" && (
                      <div className="flex items-start gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                        <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-emerald-500" />
                        <div>
                          <p className="text-sm font-medium text-emerald-500">Domain verified</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Your custom domain is active. Your workspace is now accessible at{" "}
                            <span className="font-mono font-medium text-foreground">https://{domainConfig.custom_domain}</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-border">
                    <div className="p-4">
                      <p className="text-base font-semibold">Add Custom Domain</p>
                      <p className="text-sm text-muted-foreground">
                        Enter the subdomain or domain you'd like to use for your workspace.
                      </p>
                    </div>
                    <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="custom-domain">Domain name</Label>
                        <Input
                          id="custom-domain"
                          type="text"
                          placeholder="analytics.yourcompany.com"
                          value={customDomain}
                          onChange={(e) => setCustomDomain(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSaveDomain()}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We recommend using a subdomain (e.g. <span className="font-mono">data.yourcompany.com</span>).
                        After saving, you'll be guided to configure your DNS records.
                      </p>
                      <div className="flex justify-end">
                        <Button onClick={handleSaveDomain} disabled={domainSaving || !customDomain.trim()}>
                          {domainSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                              Saving...
                            </>
                          ) : (
                            "Add Domain"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Self Hosting — Starrocks Datawarehouse */}
            {active === "datawarehouse" && (
              <>
                <div>
                  <h1 className="text-lg font-semibold">Self Hosting</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure your self-hosted services.
                  </p>
                </div>

                <div className="rounded-lg border border-primary/30 bg-primary/5 p-5">
                  <div className="flex items-start gap-3">
                    <Database className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        Looking to self-host Databull?
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        If you are looking to deploy and self-host the entire Databull application in your own server environment, please refer to our comprehensive setup guide.
                      </p>
                      <a
                        href="https://docs.google.com/document/d/1V114czTpgEaK3bNsNpkgJZYD3xK6dRAwGaAzp6Q1o3w/edit?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                      >
                        View Self-Hosting Documentation
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3">
                  <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Provide your own credentials or leave blank to use DataBull's defaults. All values are encrypted at rest.
                  </p>
                </div>

                {keysLoading ? (
                  <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading keys...
                  </div>
                ) : (
                  <>
                    <Collapsible className="rounded-lg border border-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold">Email Provider (Resend)</p>
                            <SavedBadge keyName="resend_api_key" />
                          </div>
                          <p className="text-sm text-muted-foreground">Configure Resend for transactional emails.</p>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="resend-key">API Key</Label>
                            <div className="relative">
                              <Input
                                id="resend-key"
                                type={showResendKey ? "text" : "password"}
                                placeholder={savedKeys.resend_api_key ? "Enter new key to replace..." : "re_..."}
                                value={resendKey}
                                onChange={(e) => setResendKey(e.target.value)}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowResendKey(!showResendKey)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showResendKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => saveKey("resend_api_key", resendKey)}
                              disabled={savingKey === "resend_api_key" || !resendKey.trim()}
                            >
                              {savingKey === "resend_api_key" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                              {savingKey === "resend_api_key" ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Collapsible className="rounded-lg border border-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold">Starrocks Connection</p>
                            {savedKeys.starrocks_host && (
                              <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                Configured
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">Provide credentials to connect to your Starrocks instance.</p>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label htmlFor="sr-host">Host</Label>
                                <SavedBadge keyName="starrocks_host" />
                              </div>
                              <Input
                                id="sr-host"
                                placeholder={savedKeys.starrocks_host ? "Enter new value to replace..." : "starrocks.example.com"}
                                value={srHost}
                                onChange={(e) => setSrHost(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label htmlFor="sr-port">Port</Label>
                                <SavedBadge keyName="starrocks_port" />
                              </div>
                              <Input
                                id="sr-port"
                                placeholder={savedKeys.starrocks_port ? "Enter new value to replace..." : "9030"}
                                value={srPort}
                                onChange={(e) => setSrPort(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="sr-user">Username</Label>
                              <SavedBadge keyName="starrocks_user" />
                            </div>
                            <Input
                              id="sr-user"
                              placeholder={savedKeys.starrocks_user ? "Enter new value to replace..." : "root"}
                              value={srUser}
                              onChange={(e) => setSrUser(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="sr-password">Password</Label>
                              <SavedBadge keyName="starrocks_password" />
                            </div>
                            <div className="relative">
                              <Input
                                id="sr-password"
                                type={showSrPassword ? "text" : "password"}
                                placeholder="••••••••"
                                value={srPassword}
                                onChange={(e) => setSrPassword(e.target.value)}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSrPassword(!showSrPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showSrPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => saveMultipleKeys([
                                { name: "starrocks_host", value: srHost },
                                { name: "starrocks_port", value: srPort },
                                { name: "starrocks_user", value: srUser },
                                { name: "starrocks_password", value: srPassword },
                              ])}
                              disabled={savingKey !== null || (!srHost.trim() && !srPort.trim() && !srUser.trim() && !srPassword.trim())}
                            >
                              {savingKey ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                              {savingKey ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deployment;
